import styled from 'styled-components/macro';
import { shape, number, func } from 'prop-types';
import first from 'lodash.first';
import { clientPoint } from 'd3-selection';
import { utcDay, utcFriday, utcMonth, utcYear } from 'd3-time';
import detectIt from 'detect-it';

import periodPropType from './prop-types/period';

export const HitboxRect = styled('rect')`
  fill: transparent;
  -webkit-tap-highlight-color: transparent;
`;

const round = {
  day: utcDay.round,
  week: utcFriday.round,
  month: utcMonth.round,
  year: utcYear.round,
};

/**
 * Curries a function with the correct period and x-scale
 *
 * @param {Function} x A d3-scale time scale
 * @param {String} period
 * @returns {Function} An event handler for the current period and scale
 */
function handleEvent({ x, period, weekAmount, width }) {
  return function (evt, fn) {
    const [clientX] = clientPoint(
      evt.target,
      'touches' in evt ? first(evt.touches) : evt
    );
    /** To set tempSelectedEntity for TimeBoxing */
    if (typeof weekAmount === 'number') {
      const trackingWeek = weekAmount - Math.round(x.invert(clientX));
      fn(trackingWeek);
    } else {
      /** Standard case to set date for tempSelectedEntity */
      const timestamp = round[period](x.invert(clientX)).getTime();
      fn(timestamp, evt.clientX, width);
    }
  };
}

/**
 * Returns a transparent rectangle, set up to handle all events in the chart.
 *
 * @export
 * @param {any} props
 * @returns {React.Component} A React.Component
 */
export default function Hitbox({
  size,
  period,
  x,
  weekAmount,
  handleMouseHover,
  handleMouseClick,
  handleDragChange,
  ...props
}) {
  // Create an event handler.
  const eventHandler = handleEvent({
    x,
    period,
    weekAmount,
    width: size.width,
  });
  const { hasTouch, hasMouse } = detectIt;

  return (
    <HitboxRect
      {...size}
      {...props}
      onTouchStart={() => hasTouch && handleDragChange(true)}
      onTouchEnd={() => hasTouch && handleDragChange(false)}
      onTouchMove={(evt) => hasTouch && eventHandler(evt, handleMouseClick)}
      onMouseMove={(evt) => hasMouse && eventHandler(evt, handleMouseHover)} // set the temporary selected period to the hovered period
      onClick={(evt) => eventHandler(evt, handleMouseClick)} // set the permanent selected period
      onMouseLeave={() => hasMouse && handleMouseHover(null)} // restore the clicked period (or rather, clear the temporary period)
    />
  );
}

Hitbox.propTypes = {
  /** The width and height of the rectangle. */
  size: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
  /** The period, required to convert dates to the correct format. */
  period: periodPropType.isRequired,
  /** A d3-scale time scale */
  x: func.isRequired,
  /** The amount of weeks for special timeboxing period */
  weekAmount: number,
  /** A function to be called on mousemove */
  handleMouseHover: func.isRequired,
  /** A function to be called on mouse click or touch */
  handleMouseClick: func.isRequired,
  /** A function to be called when a touch user starts or stops dragging */
  handleDragChange: func.isRequired,
};
